import { useState, useMemo, useEffect } from 'react';
import {
  Card, Select, Table, Button, Modal, Space, Tag, Typography, Row, Col, Popconfirm, Descriptions, message,
} from 'antd';
import {
  PlusOutlined, PlayCircleOutlined, EyeOutlined, DeleteOutlined, FileSearchOutlined,
  ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined,
} from '@ant-design/icons';
import httpClient from '@api/httpClient';
import dayjs from 'dayjs';
import {
  useDocumentTypes, useDocumentType, useDynamicDocuments,
  useCreateDynamicDocument, usePostDynamicDocument, usePreviewPosting, useDeleteDynamicDocument,
} from '@hooks/useMetadata';
import { useOrganizations } from '@hooks/useAccounting';
import { metadataApi } from '@api/metadataApi';
import { metadataFormApi } from '@api/importApi';
import type { FormLayout, FormNode } from '@api/importApi';
import BasFormRenderer from './BasFormRenderer';
import { ErrorBoundary } from '@components/ErrorBoundary';
import type { MetaAttributeDto, DynamicDocumentDto, PostingPreviewLine, RunFormEventResponse } from '@/types/metadata';

const { Title, Text } = Typography;

const NEW_DOC_ID = '';

interface Props {
  typeCode?: string;
  /** Опційний колбек "Назад" — рендерить кнопку у toolbar (для роботи всередині ConfigurationPage). */
  onBack?: () => void;
}

export default function DynamicDocumentsPage({ typeCode, onBack }: Props = {}) {
  const [acqSelectedType, acqSetSelectedType] = useState<string | undefined>(typeCode);
  const [acqPreviewLines, acqSetPreviewLines] = useState<PostingPreviewLine[] | null>(null);
  const [acqPreviewDoc, acqSetPreviewDoc] = useState<DynamicDocumentDto | null>(null);
  const [acqViewDoc, acqSetViewDoc] = useState<DynamicDocumentDto | null>(null);
  const [acqViewData, acqSetViewData] = useState<Record<string, unknown>>({});
  const [acqViewRefMap, acqSetViewRefMap] = useState<Record<string, string>>({});
  const [acqViewLayoutNodes, acqSetViewLayoutNodes] = useState<FormNode[] | null>(null);
  const [acqViewAttrLabels, acqSetViewAttrLabels] = useState<Record<string, string>>({});
  // Edit-mode стейт. Snapshot data при вмиканні — для "Скасувати" існуючого документа.
  const [acqIsEditing, acqSetIsEditing] = useState(false);
  const [acqEditSnapshot, acqSetEditSnapshot] = useState<Record<string, unknown> | null>(null);
  const [acqIsSaving, acqSetIsSaving] = useState(false);

  // If a fixed typeCode is passed (route-driven), always keep it in sync.
  useEffect(() => {
    if (typeCode) acqSetSelectedType(typeCode);
  }, [typeCode]);

  const acqTypesQuery = useDocumentTypes();
  const acqTypeQuery = useDocumentType(acqSelectedType);
  const acqDocsQuery = useDynamicDocuments(acqSelectedType);
  const acqOrgsQuery = useOrganizations({ page: 1, pageSize: 100 });
  const acqCreate = useCreateDynamicDocument();
  const acqPost = usePostDynamicDocument();
  const acqPreview = usePreviewPosting();
  const acqDelete = useDeleteDynamicDocument();

  const acqTypes = acqTypesQuery.data ?? [];
  const acqCurrentType = acqTypeQuery.data;
  const acqDocs = acqDocsQuery.data ?? [];
  const acqOrganizations = acqOrgsQuery.data?.items ?? [];
  const acqIsTypeFixed = Boolean(typeCode);
  const acqIsNewDoc = acqViewDoc?.id === NEW_DOC_ID;

  const acqColumns = useMemo(() => [
    {
      title: '№ документа', dataIndex: 'docNumber', key: 'docNumber',
      render: (acqVal: string | null) => acqVal ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Дата', dataIndex: 'docDate', key: 'docDate',
      render: (acqVal: string) => dayjs(acqVal).format('DD.MM.YYYY'),
    },
    {
      title: 'Сума', dataIndex: 'totalAmount', key: 'totalAmount',
      render: (acqVal: number) => acqVal.toFixed(2),
    },
    {
      title: 'Стан', dataIndex: 'isPosted', key: 'isPosted',
      render: (acqVal: boolean) => acqVal ? <Tag color="green">Проведено</Tag> : <Tag>Чернетка</Tag>,
    },
    {
      title: 'Реквізити (JSONB)', dataIndex: 'dataJson', key: 'dataJson',
      render: (acqVal: string) => (
        <Text code style={{ fontSize: 11 }}>
          {acqVal.length > 80 ? acqVal.slice(0, 80) + '…' : acqVal}
        </Text>
      ),
    },
    {
      title: 'Дії', key: 'actions',
      render: (_: unknown, acqRow: DynamicDocumentDto) => (
        <Space>
          <Button size="small" icon={<FileSearchOutlined />} onClick={() => acqHandleView(acqRow)}>
            Відкрити
          </Button>
          <Button size="small" icon={<EyeOutlined />} onClick={() => acqHandlePreview(acqRow)} disabled={acqRow.isPosted}>
            Preview
          </Button>
          <Button
            size="small" type="primary" icon={<PlayCircleOutlined />}
            onClick={() => acqPost.mutate(acqRow.id)}
            disabled={acqRow.isPosted}
          >
            Провести
          </Button>
          <Popconfirm title="Видалити документ?" onConfirm={() => acqDelete.mutate(acqRow.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} disabled={acqRow.isPosted} />
          </Popconfirm>
        </Space>
      ),
    },
  ], [acqPost, acqDelete]);

  const acqCloseView = () => {
    acqSetViewDoc(null); acqSetViewData({}); acqSetViewRefMap({});
    acqSetViewLayoutNodes(null); acqSetViewAttrLabels({});
    acqSetIsEditing(false); acqSetEditSnapshot(null);
  };

  const acqStartEdit = () => {
    acqSetEditSnapshot(acqViewData); // snapshot для відкату
    acqSetIsEditing(true);
  };

  const acqCancelEdit = () => {
    if (acqIsNewDoc) {
      // Для нового документа Cancel = повернутися до списку без збереження
      acqCloseView();
      return;
    }
    if (acqEditSnapshot) acqSetViewData(acqEditSnapshot);
    acqSetIsEditing(false);
    acqSetEditSnapshot(null);
  };

  const acqSaveEdit = async () => {
    if (!acqViewDoc) return;
    acqSetIsSaving(true);
    try {
      // Витягаємо системні поля (number/date/posted) — вони з шапки Document, не з data JSONB.
      const { number: acqNumber, date: acqDate, posted: _acqPosted, ...acqDataOnly } = acqViewData;
      const acqDocNumber = typeof acqNumber === 'string' ? acqNumber : (acqViewDoc.docNumber ?? null);
      const acqDocDate = typeof acqDate === 'string' ? acqDate : acqViewDoc.docDate;
      const acqDataJson = JSON.stringify(acqDataOnly);

      if (acqIsNewDoc) {
        if (!acqViewDoc.organizationId) {
          message.error('Не задано організацію');
          return;
        }
        const acqNewId = await acqCreate.mutateAsync({
          docTypeCode: acqViewDoc.docTypeCode,
          docNumber: acqDocNumber,
          docDate: acqDocDate,
          organizationId: acqViewDoc.organizationId,
          totalAmount: Number(acqViewDoc.totalAmount) || 0,
          comment: acqViewDoc.comment,
          dataJson: acqDataJson,
        });
        acqSetIsEditing(false);
        acqSetEditSnapshot(null);
        // Перезавантажуємо щойно створений документ як існуючий
        const acqCreated = await metadataApi.getDynamicDocument(acqNewId);
        await acqHandleView(acqCreated);
      } else {
        await httpClient.put(`/dynamic-documents/${acqViewDoc.id}`, {
          docNumber: acqDocNumber,
          docDate: acqDocDate,
          totalAmount: acqViewDoc.totalAmount,
          comment: acqViewDoc.comment,
          dataJson: acqDataJson,
        });
        message.success('Збережено');
        acqSetIsEditing(false);
        acqSetEditSnapshot(null);
        await acqHandleView(acqViewDoc);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? e?.response?.data?.title ?? e.message);
    } finally {
      acqSetIsSaving(false);
    }
  };

  const acqLoadLayoutForType = async (acqDocTypeCode: string): Promise<{
    nodes: FormNode[] | null;
    labels: Record<string, string>;
  }> => {
    const acqType = await metadataApi.getDocumentType(acqDocTypeCode);
    let acqNodes: FormNode[] | null = null;
    if (acqType?.layoutJson) {
      try {
        const acqLayout = JSON.parse(acqType.layoutJson) as FormLayout;
        acqNodes = acqLayout.nodes ?? null;
      } catch (e) {
        console.warn('Bad layoutJson for', acqDocTypeCode, e);
      }
    }
    const acqLabels: Record<string, string> = {};
    for (const a of acqType?.attributes ?? []) acqLabels[a.code] = a.name;
    return { nodes: acqNodes, labels: acqLabels };
  };

  const acqStartCreate = async () => {
    if (!acqCurrentType) return;
    if (acqOrganizations.length === 0) {
      message.error('Немає жодної організації для прив\'язки документа. Створіть організацію у довідниках.');
      return;
    }
    const acqNowIso = dayjs().toISOString();
    const acqDraft: DynamicDocumentDto = {
      id: NEW_DOC_ID,
      documentId: NEW_DOC_ID,
      docTypeCode: acqCurrentType.code,
      docNumber: '',
      docDate: acqNowIso,
      organizationId: acqOrganizations[0].id,
      totalAmount: 0,
      comment: null,
      isPosted: false,
      dataJson: '{}',
    };
    // BAS-форма очікує number/date/posted на першому рівні data
    const acqInitialData: Record<string, unknown> = {
      number: '',
      date: acqNowIso,
      posted: false,
    };
    // Defaults з MetaAttribute поки відсутні (немає DefaultValue) — лишаємо порожні.

    const { nodes, labels } = await acqLoadLayoutForType(acqCurrentType.code);
    acqSetViewDoc(acqDraft);
    acqSetViewData(acqInitialData);
    acqSetViewRefMap({});
    acqSetViewLayoutNodes(nodes);
    acqSetViewAttrLabels(labels);
    acqSetEditSnapshot(null);
    acqSetIsEditing(true);
  };

  const acqHandleView = async (acqRow: DynamicDocumentDto) => {
    try {
      const acqFull = await metadataApi.getDynamicDocument(acqRow.id);
      const acqParsed = acqFull.dataJson ? JSON.parse(acqFull.dataJson) : {};
      // BAS-форма очікує Number/Date/Posted на першому рівні data — інакше "Номер"/"від" поля порожні.
      // Document має ці поля окремо у шапці, переносимо в data для рендеру.
      const acqMerged: Record<string, unknown> = {
        ...acqParsed,
        number: acqFull.docNumber ?? acqParsed.number ?? '',
        date: acqFull.docDate ?? acqParsed.date,
        posted: acqFull.isPosted,
      };
      acqSetViewDoc(acqFull);
      acqSetViewData(acqMerged);

      // Layout з БД (Phase 0 → MetaDocumentType.LayoutJson). Якщо нема — fallback на flat-рендер.
      const { nodes, labels } = await acqLoadLayoutForType(acqFull.docTypeCode);
      acqSetViewLayoutNodes(nodes);
      acqSetViewAttrLabels(labels);

      // Резолвимо UUID-значення у назви довідників
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const acqUuids: string[] = [];
      const walk = (v: unknown) => {
        if (typeof v === 'string' && UUID_RE.test(v) && v !== '00000000-0000-0000-0000-000000000000') acqUuids.push(v);
        else if (Array.isArray(v)) v.forEach(walk);
        else if (v && typeof v === 'object') Object.values(v as Record<string, unknown>).forEach(walk);
      };
      walk(acqMerged);
      if (acqUuids.length > 0) {
        const acqResolved = await metadataFormApi.resolveRefs([...new Set(acqUuids)]);
        const acqMap: Record<string, string> = {};
        for (const r of acqResolved) if (r.displayName) acqMap[r.uuid] = r.displayName;
        acqSetViewRefMap(acqMap);
      } else {
        acqSetViewRefMap({});
      }
    } catch (e) {
      console.error('Failed to load document', e);
    }
  };

  const acqHandlePreview = async (acqRow: DynamicDocumentDto) => {
    const acqLines = await acqPreview.mutateAsync(acqRow.id);
    acqSetPreviewDoc(acqRow);
    acqSetPreviewLines(acqLines);
  };

  // Якщо нема BAS-layout, але потрібен edit/create — синтезуємо плаский layout
  // з MetaAttribute (header: number+date, тіло: атрибути).
  const acqEffectiveLayout = useMemo<FormNode[] | null>(() => {
    if (acqViewLayoutNodes) return acqViewLayoutNodes;
    if (!acqIsEditing) return null; // view-mode без layout → DocFieldList
    if (!acqCurrentType) return null;
    return buildFallbackLayout(acqCurrentType.attributes);
  }, [acqViewLayoutNodes, acqIsEditing, acqCurrentType]);

  // Мапа атрибутів за code — для ReferencePicker у BasFormRenderer (Phase 4-lite)
  const acqAttributesByCode = useMemo<Record<string, MetaAttributeDto>>(() => {
    if (!acqCurrentType) return {};
    const acqMap: Record<string, MetaAttributeDto> = {};
    for (const a of acqCurrentType.attributes) acqMap[a.code] = a;
    return acqMap;
  }, [acqCurrentType]);

  // Set полів-тригерів OnChange (Phase 5) — викликаємо engine лише для них
  const acqOnChangeFields = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const h of acqCurrentType?.eventHandlers ?? []) {
      if (h.event === 'OnChange' && h.attributeCode) set.add(h.attributeCode);
    }
    return set;
  }, [acqCurrentType]);

  // Set "section.fieldCode" — для OnRowChange (per-cell handler)
  const acqOnRowChangeFields = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const h of acqCurrentType?.eventHandlers ?? []) {
      if (h.event === 'OnRowChange' && h.attributeCode) set.add(h.attributeCode);
    }
    return set;
  }, [acqCurrentType]);

  // Wrapper: BasFormRenderer onChange → виявити змінений ключ → якщо є OnChange handler → виклик engine
  const acqHandleDataChange = async (acqNewData: Record<string, unknown>) => {
    const acqOldData = acqViewData;
    // Знаходимо одне змінене поле (для OnChange це майже завжди 1 ключ)
    let acqChangedKey: string | null = null;
    for (const k of Object.keys(acqNewData)) {
      if (!Object.is(acqNewData[k], acqOldData[k])) {
        acqChangedKey = k;
        break;
      }
    }
    // Optimistic update
    acqSetViewData(acqNewData);

    if (!acqChangedKey || !acqOnChangeFields.has(acqChangedKey) || !acqCurrentType) return;

    try {
      const acqResp = await httpClient.post<RunFormEventResponse>('/dynamic-documents/preview-event', {
        docTypeCode: acqCurrentType.code,
        event: 'OnChange',
        attributeCode: acqChangedKey,
        header: acqNewData,
        oldValue: acqOldData[acqChangedKey],
        newValue: acqNewData[acqChangedKey],
      });
      if (acqResp.data.ok && acqResp.data.handlersExecuted > 0) {
        acqSetViewData(acqResp.data.header);
      } else if (!acqResp.data.ok && acqResp.data.errorMessage) {
        message.warning(`OnChange handler: ${acqResp.data.errorMessage}`);
      }
    } catch (e: any) {
      console.warn('OnChange engine failed:', e);
    }
  };

  // Per-cell handler — викликається BasFormRenderer'ом коли змінюється комірка таблиці.
  const acqHandleCellChange = async (acqInfo: {
    tableSection: string; rowIndex: number; fieldCode: string;
    oldValue: unknown; newValue: unknown; nextData: Record<string, unknown>;
  }) => {
    if (!acqCurrentType) return;
    const acqKey = `${acqInfo.tableSection}.${acqInfo.fieldCode}`;
    if (!acqOnRowChangeFields.has(acqKey)) return; // engine не викликаємо якщо handler не зареєстровано

    try {
      const acqResp = await httpClient.post<RunFormEventResponse>('/dynamic-documents/preview-event', {
        docTypeCode: acqCurrentType.code,
        event: 'OnRowChange',
        attributeCode: acqKey,
        header: acqInfo.nextData,
        oldValue: acqInfo.oldValue,
        newValue: acqInfo.newValue,
        tableSection: acqInfo.tableSection,
        rowIndex: acqInfo.rowIndex,
      });
      if (acqResp.data.ok && acqResp.data.handlersExecuted > 0) {
        acqSetViewData(acqResp.data.header);
      } else if (!acqResp.data.ok && acqResp.data.errorMessage) {
        message.warning(`OnRowChange handler: ${acqResp.data.errorMessage}`);
      }
    } catch (e: any) {
      console.warn('OnRowChange engine failed:', e);
    }
  };

  // Повноекранний перегляд одного документа (замість модалки) — користувачу зручніше працювати на page.
  if (acqViewDoc) {
    return (
      <div>
        <Space style={{ marginBottom: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={acqCloseView}>Назад до списку</Button>
          {!acqIsEditing ? (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={acqStartEdit}
                disabled={acqViewDoc.isPosted}
              >
                Редагувати
              </Button>
              <Button
                type="primary" icon={<PlayCircleOutlined />}
                onClick={() => { if (acqViewDoc) acqPost.mutate(acqViewDoc.id); acqCloseView(); }}
                disabled={acqViewDoc.isPosted}
              >
                Провести
              </Button>
              <Popconfirm title="Видалити документ?" onConfirm={() => { if (acqViewDoc) acqDelete.mutate(acqViewDoc.id); acqCloseView(); }}>
                <Button danger icon={<DeleteOutlined />} disabled={acqViewDoc.isPosted}>Видалити</Button>
              </Popconfirm>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} loading={acqIsSaving} onClick={acqSaveEdit}>
                {acqIsNewDoc ? 'Створити' : 'Зберегти'}
              </Button>
              <Button icon={<CloseOutlined />} onClick={acqCancelEdit} disabled={acqIsSaving}>
                Скасувати
              </Button>
              <Tag color={acqIsNewDoc ? 'blue' : 'orange'}>
                {acqIsNewDoc ? 'Новий документ' : 'Редагування'}
              </Tag>
            </>
          )}
        </Space>
        <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          {acqCurrentType?.name ?? acqViewDoc.docTypeCode}
          {!acqIsNewDoc && <> № {acqViewDoc.docNumber ?? '—'} від {dayjs(acqViewDoc.docDate).format('DD.MM.YYYY')}</>}
        </Title>
        {!acqIsNewDoc && (
          <Space size="large" style={{ marginBottom: 12 }}>
            <Text type="secondary">Сума: <b>{acqViewDoc.totalAmount.toFixed(2)}</b></Text>
            <Text type="secondary">Стан: {acqViewDoc.isPosted ? <Tag color="green">Проведено</Tag> : <Tag>Чернетка</Tag>}</Text>
            {acqViewDoc.comment && <Text type="secondary">Коментар: {acqViewDoc.comment}</Text>}
          </Space>
        )}
        <Card>
          {acqEffectiveLayout ? (
            <ErrorBoundary label="BasFormRenderer">
              <BasFormRenderer
                nodes={acqEffectiveLayout}
                data={acqViewData}
                refMap={acqViewRefMap}
                mode={acqIsEditing ? 'edit' : 'view'}
                onChange={acqIsEditing ? acqHandleDataChange : undefined}
                onCellChange={acqIsEditing ? acqHandleCellChange : undefined}
                attributesByCode={acqAttributesByCode}
                attributes={acqCurrentType?.attributes ?? []}
              />
            </ErrorBoundary>
          ) : (
            <DocFieldList data={acqViewData} refMap={acqViewRefMap} attrLabels={acqViewAttrLabels} />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        {onBack && <Button icon={<ArrowLeftOutlined />} onClick={onBack}>До списку типів</Button>}
        <Title level={3} style={{ margin: 0 }}>
          {acqIsTypeFixed && acqCurrentType ? acqCurrentType.name : 'Кастомні документи'}
        </Title>
      </Space>
      <div>
        <Text type="secondary">
          {acqIsTypeFixed
            ? 'Документи, створені через метадата-движок.'
            : 'Документи, створені через метадата-движок. Реквізити зберігаються в JSONB.'}
        </Text>
      </div>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={16} align="middle">
          {!acqIsTypeFixed && (
            <>
              <Col>
                <Text strong>Тип документа:</Text>
              </Col>
              <Col flex="auto">
                <Select
                  style={{ width: 400 }}
                  placeholder="Оберіть тип"
                  value={acqSelectedType}
                  onChange={acqSetSelectedType}
                  options={acqTypes.map((acqT) => ({
                    value: acqT.code,
                    label: `${acqT.name}${acqT.navGroup ? ` · ${acqT.navGroup}` : ''}`,
                  }))}
                />
              </Col>
            </>
          )}
          {acqIsTypeFixed && <Col flex="auto" />}
          <Col>
            <Button
              type="primary" icon={<PlusOutlined />}
              disabled={!acqSelectedType || !acqCurrentType}
              loading={acqCreate.isPending}
              onClick={acqStartCreate}
            >
              Створити
            </Button>
          </Col>
        </Row>
      </Card>

      {acqSelectedType && (
        <Card style={{ marginTop: 16 }}>
          <Table<DynamicDocumentDto>
            rowKey="id"
            columns={acqColumns}
            dataSource={acqDocs}
            loading={acqDocsQuery.isLoading}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      <Modal
        open={acqPreviewLines !== null}
        title={`Preview проводок: ${acqPreviewDoc?.docNumber ?? ''}`}
        onCancel={() => { acqSetPreviewLines(null); acqSetPreviewDoc(null); }}
        onOk={() => {
          if (acqPreviewDoc) acqPost.mutate(acqPreviewDoc.id);
          acqSetPreviewLines(null);
          acqSetPreviewDoc(null);
        }}
        okText="Провести"
        cancelText="Закрити"
        width={720}
      >
        {acqPreviewDoc && (
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="№">{acqPreviewDoc.docNumber ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Дата">{dayjs(acqPreviewDoc.docDate).format('DD.MM.YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Сума">{acqPreviewDoc.totalAmount.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="Тип">{acqPreviewDoc.docTypeCode}</Descriptions.Item>
          </Descriptions>
        )}
        <Table
          rowKey="lineNo"
          size="small"
          pagination={false}
          dataSource={acqPreviewLines ?? []}
          columns={[
            { title: '№', dataIndex: 'lineNo', width: 50 },
            { title: 'Дт', dataIndex: 'debitAccount', width: 80 },
            { title: 'Кт', dataIndex: 'creditAccount', width: 80 },
            { title: 'Сума', dataIndex: 'amount', render: (acqV: number) => acqV.toFixed(2) },
            { title: 'Субконто Дт', dataIndex: 'dtSubconto1Id', render: (acqV: string | null) => acqV ? <Text code style={{ fontSize: 10 }}>{acqV.slice(0, 8)}…</Text> : '—' },
            { title: 'Субконто Кт', dataIndex: 'ctSubconto1Id', render: (acqV: string | null) => acqV ? <Text code style={{ fontSize: 10 }}>{acqV.slice(0, 8)}…</Text> : '—' },
          ]}
        />
      </Modal>
    </div>
  );
}

const UUID_RE_LOCAL = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T/;

function buildFallbackLayout(attributes: MetaAttributeDto[]): FormNode[] {
  // Групуємо за section. Header → блок реквізитів шапки. Кожна табл. частина → окрема Table-нода.
  const acqHeaderAttrs = attributes.filter((a) => a.section === 'header').sort((a, b) => a.orderIdx - b.orderIdx);
  const acqTabSections = new Map<string, MetaAttributeDto[]>();
  for (const a of attributes) {
    if (a.section !== 'header') {
      const arr = acqTabSections.get(a.section) ?? [];
      arr.push(a);
      acqTabSections.set(a.section, arr);
    }
  }

  const acqNodes: FormNode[] = [];

  // Шапка: number + date першими, потім решта вертикально
  const acqHeaderTopBar: FormNode = {
    type: 'group',
    orientation: 'horizontal',
    showTitle: false,
    children: [
      { type: 'field', attributeCode: 'number', title: 'Номер', width: 14 },
      { type: 'field', attributeCode: 'date', title: 'Дата', width: 14 },
    ],
  };
  const acqHeaderBody: FormNode = {
    type: 'group',
    orientation: 'vertical',
    title: 'Реквізити',
    showTitle: true,
    children: acqHeaderAttrs.map((a) => ({
      type: a.dataType === 'bool' ? 'checkbox' : 'field',
      attributeCode: a.code,
      title: a.name,
      width: 30,
    })),
  };
  acqNodes.push(acqHeaderTopBar);
  if (acqHeaderAttrs.length > 0) acqNodes.push(acqHeaderBody);

  // Табличні частини
  for (const [section, attrs] of acqTabSections) {
    const sortedAttrs = attrs.sort((a, b) => a.orderIdx - b.orderIdx);
    acqNodes.push({
      type: 'table',
      title: section,
      rowsPath: section,
      columns: [
        { rowFieldCode: '_line', title: '#', width: 5 },
        ...sortedAttrs.map((a) => ({ rowFieldCode: a.code, title: a.name, width: 20 })),
      ],
    });
  }

  return acqNodes;
}

function DocFieldList({
  data,
  refMap,
  attrLabels,
}: {
  data: Record<string, unknown>;
  refMap: Record<string, string>;
  attrLabels: Record<string, string>;
}) {
  const entries = Object.entries(data).filter(([, v]) => !Array.isArray(v));
  const tables = Object.entries(data).filter(([, v]) => Array.isArray(v) && v.length > 0);

  const fmt = (v: unknown): React.ReactNode => {
    if (v === null || v === undefined || v === '') return <Text type="secondary">—</Text>;
    if (typeof v === 'boolean') return v ? '✓' : '—';
    const s = String(v);
    if (s === ZERO_UUID) return <Text type="secondary">—</Text>;
    if (UUID_RE_LOCAL.test(s)) {
      const name = refMap[s];
      return name ?? <Text type="secondary" style={{ fontSize: 12 }}>UUID: {s.slice(0, 8)}…</Text>;
    }
    if (ISO_DATE_RE.test(s)) {
      const d = dayjs(s);
      if (d.isValid()) return d.format('DD.MM.YYYY');
    }
    return s;
  };

  const labelOf = (code: string) => attrLabels[code] ?? code;

  return (
    <>
      <Descriptions size="small" column={1} bordered labelStyle={{ width: 240 }}>
        {entries.map(([k, v]) => (
          <Descriptions.Item key={k} label={labelOf(k)}>{fmt(v)}</Descriptions.Item>
        ))}
      </Descriptions>
      {tables.map(([k, arr]) => {
        const rows = arr as Record<string, unknown>[];
        const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
        return (
          <div key={k} style={{ marginTop: 12 }}>
            <Title level={5}>Таблична частина: {labelOf(k)} ({rows.length})</Title>
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              dataSource={rows}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={cols.map((c) => ({
                title: labelOf(c),
                key: c,
                render: (_: unknown, row: Record<string, unknown>) => fmt(row[c]),
              }))}
            />
          </div>
        );
      })}
    </>
  );
}

